import api from './api';
import type { AdminChartPoint, AdminStatsResponse, BookingRecord, BookingRequestData, BookingStatus, BookingUpdateRequestData, ConsultationServiceItem, ConsultationServiceRequestData, ContactMessage, Role, ServiceItem, ServiceRequestData, TeamMemberRecord, TeamMemberRequestData, UserRecord, PageResponse, UserStatisticsResponse, BookingStatisticsResponse } from '../types';

export const adminService = {
  // Stats & Overview
  getUsers: async (search?: string, role?: string, page?: number, size?: number): Promise<PageResponse<UserRecord>> => {
    const searchParams: Record<string, string | number> = {};
    if (search !== undefined && search !== '') searchParams.search = search;
    if (role !== undefined && role !== '') searchParams.role = role;
    if (page !== undefined) searchParams.page = page;
    if (size !== undefined) searchParams.size = size;
    return api.get('users', { searchParams }).json<PageResponse<UserRecord>>();
  },
  getUsersStatistics: async (): Promise<UserStatisticsResponse> => {
    return api.get('users/statistics').json<UserStatisticsResponse>();
  },
  getBookings: async (
    search?: string,
    status?: string,
    serviceName?: string,
    page?: number,
    size?: number
  ): Promise<PageResponse<BookingRecord>> => {
    const searchParams: Record<string, string | number> = {};
    if (search !== undefined && search !== '') searchParams.search = search;
    if (status !== undefined && status !== '') searchParams.status = status;
    if (serviceName !== undefined && serviceName !== '') searchParams.serviceName = serviceName;
    if (page !== undefined) searchParams.page = page;
    if (size !== undefined) searchParams.size = size;
    return api.get('bookings', { searchParams }).json<PageResponse<BookingRecord>>();
  },
  getBookingStatistics: async (): Promise<BookingStatisticsResponse> => {
    return api.get('bookings/statistics').json<BookingStatisticsResponse>();
  },
  getContactMessages: async (page?: number, size?: number): Promise<PageResponse<ContactMessage>> => {
    const searchParams: Record<string, string | number> = {};
    if (page !== undefined) searchParams.page = page;
    if (size !== undefined) searchParams.size = size;
    return api.get('contact', { searchParams }).json<PageResponse<ContactMessage>>();
  },
  deleteContactMessage: async (messageId: string): Promise<void> => {
    await api.delete(`contact/${messageId}`);
  },
  getStats: async (): Promise<AdminStatsResponse> => {
    return api.get('admin/stats').json<AdminStatsResponse>();
  },
  getChartData: async (range: string): Promise<AdminChartPoint[]> => {
    return api.get(`admin/charts?range=${range}`).json<AdminChartPoint[]>();
  },

  // User Management
  updateUser: async (userId: string, data: { name: string; email: string; role: Role }): Promise<UserRecord> => {
    return api.put(`users/${userId}`, { json: data }).json<UserRecord>();
  },
  deleteUser: async (userId: string): Promise<void> => {
    await api.delete(`users/${userId}`);
  },

  // Booking Status Updates
  updateBookingStatus: async (bookingId: string, status: BookingStatus): Promise<BookingRecord> => {
    return api.patch(`bookings/${bookingId}/status`, { json: { status } }).json<BookingRecord>();
  },

  // Booking Management
  createBooking: async (data: BookingRequestData): Promise<BookingRecord> => {
    return api.post('bookings/admin', { json: data }).json<BookingRecord>();
  },
  updateBooking: async (bookingId: string, data: BookingUpdateRequestData): Promise<BookingRecord> => {
    return api.put(`bookings/${bookingId}`, { json: data }).json<BookingRecord>();
  },
  deleteBooking: async (bookingId: string): Promise<void> => {
    await api.delete(`bookings/${bookingId}`);
  },

  // Service Management
  getServices: async (search?: string, page?: number, size?: number): Promise<PageResponse<ServiceItem>> => {
    const searchParams: Record<string, string | number> = {};
    if (search !== undefined && search !== '') searchParams.search = search;
    if (page !== undefined) searchParams.page = page;
    if (size !== undefined) searchParams.size = size;
    return api.get('services/admin', { searchParams }).json<PageResponse<ServiceItem>>();
  },
  createService: async (data: ServiceRequestData): Promise<ServiceItem> => {
    return api.post('services', { json: data }).json<ServiceItem>();
  },
  updateService: async (serviceId: string, data: ServiceRequestData): Promise<ServiceItem> => {
    return api.put(`services/${serviceId}`, { json: data }).json<ServiceItem>();
  },
  deleteService: async (serviceId: string): Promise<void> => {
    await api.delete(`services/${serviceId}`);
  },

  // Consultation Service Management
  getConsultationServices: async (search?: string, page?: number, size?: number): Promise<PageResponse<ConsultationServiceItem>> => {
    const searchParams: Record<string, string | number> = {};
    if (search !== undefined && search !== '') searchParams.search = search;
    if (page !== undefined) searchParams.page = page;
    if (size !== undefined) searchParams.size = size;
    return api.get('consultation-services/admin', { searchParams }).json<PageResponse<ConsultationServiceItem>>();
  },
  createConsultationService: async (data: ConsultationServiceRequestData): Promise<ConsultationServiceItem> => {
    return api.post('consultation-services', { json: data }).json<ConsultationServiceItem>();
  },
  updateConsultationService: async (serviceId: string, data: ConsultationServiceRequestData): Promise<ConsultationServiceItem> => {
    return api.put(`consultation-services/${serviceId}`, { json: data }).json<ConsultationServiceItem>();
  },
  deleteConsultationService: async (serviceId: string): Promise<void> => {
    await api.delete(`consultation-services/${serviceId}`);
  },

  // Team Management
  getTeam: async (search?: string, page?: number, size?: number): Promise<PageResponse<TeamMemberRecord>> => {
    const searchParams: Record<string, string | number> = {};
    if (search !== undefined && search !== '') searchParams.search = search;
    if (page !== undefined) searchParams.page = page;
    if (size !== undefined) searchParams.size = size;
    return api.get('team/admin', { searchParams }).json<PageResponse<TeamMemberRecord>>();
  },
  createTeamMember: async (data: TeamMemberRequestData): Promise<TeamMemberRecord> => {
    return api.post('team', { json: data }).json<TeamMemberRecord>();
  },
  updateTeamMember: async (memberId: string, data: TeamMemberRequestData): Promise<TeamMemberRecord> => {
    return api.put(`team/${memberId}`, { json: data }).json<TeamMemberRecord>();
  },
  deleteTeamMember: async (memberId: string): Promise<void> => {
    await api.delete(`team/${memberId}`);
  },

  // Asset Upload (multipart — ky sets the Content-Type boundary automatically)
  uploadImage: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('assets', { body: formData }).json<{ url: string }>();
  },
};
